import {
  FaCheckCircle,
  FaFileImage,
  FaReceipt,
  FaDownload,
  FaFilePdf,
} from "react-icons/fa";
import { OrderPaymentDetails } from "features/stock-ordering/core/domain/payment.model";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import { dateSetup } from "./stock-ordering-utils";
import { useState } from "react";
import { Dialog, DialogContent, DialogActions, Button } from "@mui/material";

interface PaymentProcessCompleteProps {
  paymentDetails: OrderPaymentDetails | null | undefined;
}

// Helper to check if a filename is an image
const isImageFile = (filename: string | null | undefined): boolean => {
  if (!filename) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  const lowerFilename = filename.toLowerCase();
  return imageExtensions.some((ext) => lowerFilename.endsWith(ext));
};

// Helper to get the full file URL for payment proof
const getFileUrl = (filename: string | null | undefined): string => {
  if (!filename) return "";
  return `${REACT_APP_DOMAIN_URL}api/assets/uploads/multim/payment/${filename}`;
};

// Helper to get the full file URL for official receipts
const getReceiptUrl = (filename: string | null | undefined): string => {
  if (!filename) return "";
  return `${REACT_APP_DOMAIN_URL}api/assets/uploads/multim/receipts/${filename}`;
};

const handleDownloadFile = (
  filename: string | null | undefined,
  isReceipt: boolean = false,
) => {
  if (!filename) return;
  const url = isReceipt ? getReceiptUrl(filename) : getFileUrl(filename);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function PaymentProcessComplete(props: PaymentProcessCompleteProps) {
  const { paymentDetails } = props;

  const [showPaymentProofModal, setShowPaymentProofModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const paymentProofIsImage = isImageFile(paymentDetails?.payment_proof_image);
  const receiptIsImage = isImageFile(paymentDetails?.official_receipt_image);

  return (
    <div className="space-y-6">
      {/* Success Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3">
          <div className="bg-white bg-opacity-20 rounded-full p-3">
            <FaCheckCircle className="text-3xl" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Payment Complete</h3>
            <p className="text-green-100">
              This order has been fully paid and confirmed.
            </p>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payment Proof Section */}
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
          <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <FaFileImage className="text-blue-600" />
            <span>Payment Proof</span>
          </h4>

          {paymentDetails?.payment_proof_image ? (
            <div className="space-y-3">
              {paymentProofIsImage ? (
                <div
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowPaymentProofModal(true)}
                >
                  <img
                    src={getFileUrl(paymentDetails.payment_proof_image)}
                    alt="Payment Proof"
                    className="w-full max-h-[200px] object-contain rounded-lg shadow-md border border-gray-200"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-2 py-4">
                  <FaFilePdf className="text-4xl text-red-500" />
                  <p className="text-sm text-gray-600">
                    {paymentDetails.payment_proof_image}
                  </p>
                </div>
              )}

              {/* Download button */}
              <div className="flex justify-center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaDownload />}
                  onClick={() =>
                    handleDownloadFile(paymentDetails.payment_proof_image)
                  }
                  sx={{
                    borderColor: "#2563eb",
                    color: "#2563eb",
                    "&:hover": {
                      borderColor: "#1d4ed8",
                      backgroundColor: "rgba(37, 99, 235, 0.04)",
                    },
                  }}
                >
                  Download
                </Button>
              </div>

              <div className="space-y-2 text-sm">
                {paymentDetails.payment_proof_uploaded_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Uploaded:</span>
                    <span className="text-gray-700 font-medium">
                      {dateSetup(
                        paymentDetails.payment_proof_uploaded_at,
                        true,
                      )}
                    </span>
                  </div>
                )}
                {paymentDetails.uploaded_by_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">By:</span>
                    <span className="text-gray-700 font-medium">
                      {paymentDetails.uploaded_by_name}
                    </span>
                  </div>
                )}
                {paymentDetails.payment_remarks && (
                  <div className="mt-2 p-2 bg-white rounded border">
                    <span className="text-xs text-gray-500 uppercase">
                      Remarks:
                    </span>
                    <p className="text-gray-700">
                      {paymentDetails.payment_remarks}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <FaFileImage className="text-4xl mx-auto mb-2" />
              <p>No payment proof available</p>
            </div>
          )}
        </div>

        {/* Official Receipt Section */}
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
          <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <FaReceipt className="text-green-600" />
            <span>Official Receipt</span>
          </h4>

          {paymentDetails?.official_receipt_image ? (
            <div className="space-y-3">
              {receiptIsImage ? (
                <div
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowReceiptModal(true)}
                >
                  <img
                    src={getReceiptUrl(paymentDetails.official_receipt_image)}
                    alt="Official Receipt"
                    className="w-full max-h-[200px] object-contain rounded-lg shadow-md border border-gray-200"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-2 py-4">
                  <FaFilePdf className="text-4xl text-red-500" />
                  <p className="text-sm text-gray-600">
                    {paymentDetails.official_receipt_image}
                  </p>
                </div>
              )}

              {/* Download button */}
              <div className="flex justify-center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaDownload />}
                  onClick={() =>
                    handleDownloadFile(
                      paymentDetails.official_receipt_image,
                      true,
                    )
                  }
                  sx={{
                    borderColor: "#16a34a",
                    color: "#16a34a",
                    "&:hover": {
                      borderColor: "#15803d",
                      backgroundColor: "rgba(22, 163, 74, 0.04)",
                    },
                  }}
                >
                  Download
                </Button>
              </div>

              <div className="space-y-2 text-sm">
                {paymentDetails.official_receipt_uploaded_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Uploaded:</span>
                    <span className="text-gray-700 font-medium">
                      {dateSetup(
                        paymentDetails.official_receipt_uploaded_at,
                        true,
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <FaReceipt className="text-4xl mx-auto mb-2" />
              <p>No official receipt uploaded</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Details */}
      <div className="bg-green-50 rounded-xl p-5 border border-green-200">
        <h4 className="text-md font-semibold text-green-800 mb-4">
          Confirmation Details
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {paymentDetails?.confirmed_at && (
            <div>
              <span className="text-gray-500">Confirmed On:</span>
              <p className="text-gray-900 font-semibold">
                {dateSetup(paymentDetails.confirmed_at, true)}
              </p>
            </div>
          )}

          {paymentDetails?.confirmed_by_name && (
            <div>
              <span className="text-gray-500">Confirmed By:</span>
              <p className="text-gray-900 font-semibold">
                {paymentDetails.confirmed_by_name}
              </p>
            </div>
          )}

          {paymentDetails?.confirmation_remarks && (
            <div className="md:col-span-2">
              <span className="text-gray-500">Confirmation Remarks:</span>
              <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                {paymentDetails.confirmation_remarks}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Proof Modal - only for images */}
      <Dialog
        open={showPaymentProofModal}
        onClose={() => setShowPaymentProofModal(false)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0 }}>
          {paymentDetails?.payment_proof_image && paymentProofIsImage && (
            <img
              src={getFileUrl(paymentDetails.payment_proof_image)}
              alt="Payment Proof Full Size"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<FaDownload />}
            onClick={() =>
              handleDownloadFile(paymentDetails?.payment_proof_image)
            }
          >
            Download
          </Button>
          <Button onClick={() => setShowPaymentProofModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Official Receipt Modal - only for images */}
      <Dialog
        open={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0 }}>
          {paymentDetails?.official_receipt_image && receiptIsImage && (
            <img
              src={getReceiptUrl(paymentDetails.official_receipt_image)}
              alt="Official Receipt Full Size"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<FaDownload />}
            onClick={() =>
              handleDownloadFile(paymentDetails?.official_receipt_image, true)
            }
          >
            Download
          </Button>
          <Button onClick={() => setShowReceiptModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
